import { computed, nextTick, ref, toValue, watch } from 'vue'
import { useGoTo } from 'vuetify/framework'
import { createSharedComposable } from '@vueuse/core'
import { useDeepBoxDeepBoxesBoxesNodesStore } from '@/stores/deepbox/deepboxes/boxes/nodes.ts'
import type { MaybeRefOrGetter } from 'vue'

export const useNodeTableScroller = createSharedComposable(() => {
  const deepBoxDeepBoxesBoxesNodesStore = useDeepBoxDeepBoxesBoxesNodesStore()
  const goTo = useGoTo()

  const tableRef = ref<MaybeRefOrGetter<
    HTMLElement | { $el: HTMLElement }
  > | null>(null)

  function init(
    refValue: MaybeRefOrGetter<HTMLElement | { $el: HTMLElement }>,
  ) {
    tableRef.value = refValue
  }

  const tableEl = computed<HTMLElement | undefined>(() => {
    const table = toValue(tableRef.value) as
      HTMLElement | { $el: HTMLElement } | undefined
    if (!table) return undefined

    return table instanceof HTMLElement ? table : table.$el
  })

  const wrapper = computed<HTMLElement | undefined>(() => {
    if (!tableEl.value) return undefined
    return (
      tableEl.value.querySelector<HTMLElement>('div.v-table__wrapper') ??
      undefined
    )
  })

  // disable browser scroll anchoring: it fights our own scrolls whenever a
  // node is unshifted above the current viewport
  watch(
    wrapper,
    (el) => {
      if (el) el.style.overflowAnchor = 'none'
    },
    { immediate: true },
  )

  async function scrollToTop() {
    await nextTick()
    if (!wrapper.value) return
    void goTo(0, { container: wrapper.value })
  }

  function nextFrame() {
    return new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve()),
    )
  }

  // a just-added row can take more than one tick to reach the DOM, so poll
  // a few frames instead of assuming a single nextTick is enough
  async function waitForNodeEl(nodeId: string, maxAttempts = 20) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await nextTick()
      const el = tableEl.value?.querySelector<HTMLElement>(`[id="${nodeId}"]`)
      if (el) return el
      await nextFrame()
    }
    return undefined
  }

  // nodes passed here are always freshly unshifted to the front of the
  // list, so the target is simply the container's top
  async function scrollToNode(
    nodeId: string,
    shouldSelectNode: boolean = false,
  ) {
    const table = toValue(tableRef)
    if (!table) return

    // confirms the store mutation actually reached the DOM before scrolling
    const el = await waitForNodeEl(nodeId)
    if (!el) {
      console.warn(`Node ${nodeId} not found`)
      return
    }

    if (shouldSelectNode) {
      deepBoxDeepBoxesBoxesNodesStore.selectedNodeIds = [nodeId]
    }

    if (!wrapper.value) return
    await goTo(0, { container: wrapper.value })
  }

  return {
    init,
    scrollToTop,
    scrollToNode,
  }
})
